import { useState, useEffect } from "react";
import {
  FaSpinner,
  FaCrown,
  FaShieldAlt,
  FaUserCog,
  FaUser,
  FaInfoCircle,
  FaTimes,
} from "react-icons/fa";
import { supabase } from "../../../supabaseClient";
import InlineAlert from "../../UI/DesignStandards/Notifications/InlineAlert";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../UI/DesignStandards";
import { usePermissions } from "../../../context/PermissionsContext";
import { useOrganization } from "../../../context/OrganizationContext";

const EditMemberRoleModal = ({ isOpen, onClose, member, onUpdate }) => {
  const permissions = usePermissions();
  const { currentOrganization } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  // Define role information with clear descriptions and icons
  const roleInfo = {
    owner: {
      name: "Owner",
      description:
        "Full control over the organization. Can manage everything including other owners.",
      icon: FaCrown,
      color: "bg-brand-charcoal text-brand-cream border-brand-charcoal",
      capabilities: [
        "Manage all members and roles",
        "Create and manage tournaments",
        "Delete the organization",
        "Access all features",
      ],
    },
    admin: {
      name: "Administrator",
      description:
        "Can manage members, tournaments, and most organization settings.",
      icon: FaShieldAlt,
      color: "bg-brand-charcoal text-brand-cream border-brand-charcoal",
      capabilities: [
        "Invite and manage members",
        "Create and manage tournaments",
        "Assign roles to members",
        "Access admin features",
      ],
    },
    manager: {
      name: "Manager",
      description:
        "Can manage tournaments and enter scores, but limited member management.",
      icon: FaUserCog,
      color: "bg-brand-cream text-brand-charcoal border-brand-charcoal/20",
      capabilities: [
        "Create and manage tournaments",
        "Enter and edit scores",
        "View member information",
        "Limited member management",
      ],
    },
    member: {
      name: "Member",
      description:
        "Basic access to view tournaments and participate in events.",
      icon: FaUser,
      color: "bg-brand-cream text-brand-charcoal border-brand-charcoal/20",
      capabilities: [
        "View tournaments and results",
        "Participate in events",
        "View member directory",
        "Basic access only",
      ],
    },
  };

  useEffect(() => {
    if (isOpen && member) {
      fetchAvailableRoles();
      setSelectedRoles(member.memberRoles || []);
    }
  }, [isOpen, member]);

  const fetchAvailableRoles = async () => {
    try {
      const { data: roles, error } = await supabase
        .from("organizationRoles")
        .select("*")
        .order("rank", { ascending: true });

      if (error) throw error;
      setAvailableRoles(roles);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setErrorMessage("Failed to load available roles");
    }
  };

  const handleRoleToggle = (role) => {
    // Check if user can modify this role
    if (!permissions.canModifyRole(role.rank)) {
      setErrorMessage("You don't have permission to assign this role");
      return;
    }

    setSelectedRoles((prev) => {
      const isSelected = prev.some((r) => r.roleID === role.id);
      if (isSelected) {
        return prev.filter((r) => r.roleID !== role.id);
      } else {
        return [...prev, { roleID: role.id, role }];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if user can modify this member
    if (!permissions.canModifyMember(member.memberRoles)) {
      setErrorMessage(
        "You don't have permission to modify this member's roles"
      );
      return;
    }

    // Safety check: Prevent removing Owner role from the last owner
    const isOwner = member.memberRoles.some(
      (role) => role.role?.name?.toLowerCase() === "owner"
    );
    const isRemovingOwner =
      isOwner &&
      !selectedRoles.some((role) => role.role?.name?.toLowerCase() === "owner");

    if (isRemovingOwner) {
      // Check if this is the last owner
      const { data: allOwners, error: ownersError } = await supabase
        .from("organizationMembers")
        .select(
          `
          id,
          memberRoles:organizationMemberRoles!inner (
            role:organizationRoles (name)
          )
        `
        )
        .eq("organizationID", currentOrganization.id)
        .eq("status", "active")
        .eq("memberRoles.isDeleted", false);

      if (!ownersError && allOwners) {
        const activeOwners = allOwners.filter((member) =>
          member.memberRoles.some(
            (role) => role.role?.name?.toLowerCase() === "owner"
          )
        );

        if (activeOwners.length <= 1) {
          setErrorMessage(
            "Cannot remove the Owner role from the last owner in the organization"
          );
          return;
        }
      }
    }

    setLoading(true);

    try {
      const currentUser = (await supabase.auth.getUser()).data.user;
      const now = new Date().toISOString();

      // Get all existing roles (including soft-deleted ones)
      const { data: existingRoles, error: fetchError } = await supabase
        .from("organizationMemberRoles")
        .select("*")
        .eq("memberID", member.id);

      if (fetchError) throw fetchError;

      // Create a map of existing roles for easy lookup
      const existingRolesMap = new Map(
        existingRoles.map((role) => [role.roleID, role])
      );

      // Get the selected role IDs
      const selectedRoleIds = selectedRoles.map((role) => role.roleID);

      // Find roles to add (selected but not currently active)
      const rolesToAdd = selectedRoles
        .filter((role) => {
          const existingRole = existingRolesMap.get(role.roleID);
          return !existingRole || existingRole.isDeleted; // Add if no record exists or if it's soft-deleted
        })
        .map((role) => ({
          memberID: member.id,
          roleID: role.roleID,
          isDeleted: false,
          deletedAt: null,
          deletedBy: null,
          createdAt: now,
          createdBy: currentUser.id,
        }));

      // Find roles to remove (existing active but not selected)
      const rolesToRemove = existingRoles
        .filter(
          (role) => !role.isDeleted && !selectedRoleIds.includes(role.roleID)
        )
        .map((role) => role.id);

      // Add new roles (creates new records for audit history)
      if (rolesToAdd.length > 0) {
        const { error: insertError } = await supabase
          .from("organizationMemberRoles")
          .insert(rolesToAdd);

        if (insertError) throw insertError;
      }

      // Soft delete roles that are no longer selected
      if (rolesToRemove.length > 0) {
        const { error: deleteError } = await supabase
          .from("organizationMemberRoles")
          .update({
            isDeleted: true,
            deletedAt: now,
            deletedBy: currentUser.id,
          })
          .in("id", rolesToRemove);

        if (deleteError) throw deleteError;
      }

      // Success: close and refresh without toast
      onUpdate();
      onClose();
    } catch (error) {
      console.error("Error updating roles:", error);
      setErrorMessage("Failed to update member roles");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-brand-charcoal/60 flex items-center justify-center p-4 z-50">
      <div className="card-board max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="heading-display text-xl">Edit Member Roles</h3>
            <button
              type="button"
              onClick={onClose}
              className="text-brand-muted hover:text-brand-charcoal"
            >
              <FaTimes className="h-5 w-5" />
            </button>
          </div>

          {errorMessage ? (
            <div className="mb-4">
              <InlineAlert
                type="error"
                title="There was a problem"
                message={errorMessage}
                onClose={() => setErrorMessage("")}
              />
            </div>
          ) : null}

          {/* Member Info */}
          <div className="card-inset p-4 mb-6">
            <div className="flex items-center">
              <div className="h-12 w-12 rounded-lg bg-brand-cream flex items-center justify-center">
                <span className="text-lg font-medium text-brand-muted">
                  {member?.user?.full_name?.charAt(0) ||
                    member?.user?.email?.charAt(0)}
                </span>
              </div>
              <div className="ml-4">
                <h4 className="text-lg font-medium text-brand-charcoal">
                  {member?.user?.full_name || member?.user?.email}
                </h4>
                <p className="text-sm text-brand-muted">{member?.user?.email}</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="flex items-center mb-4">
                <FaInfoCircle className="text-brand-muted mr-2" />
                <p className="text-sm text-brand-muted">
                  Select the roles you want to assign to this member. They will
                  have access to all features associated with their highest
                  role.
                </p>
              </div>

              {availableRoles.map((role) => {
                const roleKey = role.name.toLowerCase();
                const roleData = roleInfo[roleKey] || {
                  name: role.name,
                  description: role.description || "Custom role",
                  icon: FaUser,
                  color: "bg-brand-cream text-brand-charcoal border-brand-charcoal/20",
                  capabilities: ["Custom permissions"],
                };
                const IconComponent = roleData.icon;
                const canAssign = permissions.canModifyRole(role.rank);
                const isSelected = selectedRoles.some(
                  (r) => r.roleID === role.id
                );

                return (
                  <div
                    key={role.id}
                    className={`border rounded-lg p-4 transition-all ${
                      isSelected
                        ? "border-brand-charcoal bg-brand-cream"
                        : "border-brand-charcoal/10 bg-white hover:border-brand-charcoal/30"
                    } ${!canAssign ? "opacity-50" : ""}`}
                  >
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id={`role-${role.id}`}
                        checked={isSelected}
                        onChange={() => handleRoleToggle(role)}
                        disabled={!canAssign}
                        className={`h-5 w-5 text-brand-charcoal focus:ring-brand-gold-ink border-brand-charcoal/20 rounded mt-1 ${
                          !canAssign ? "cursor-not-allowed" : ""
                        }`}
                      />

                      <div className="ml-4 flex-1">
                        <div className="flex items-center mb-2">
                          <IconComponent className="h-5 w-5 mr-2 text-brand-muted" />
                          <label
                            htmlFor={`role-${role.id}`}
                            className={`text-lg font-medium ${
                              canAssign
                                ? "text-brand-charcoal cursor-pointer"
                                : "text-brand-mist"
                            }`}
                          >
                            {roleData.name}
                          </label>
                          {!canAssign && (
                            <span className="ml-2 text-xs text-brand-muted bg-brand-cream px-2 py-1 rounded-lg">
                              Insufficient permissions
                            </span>
                          )}
                        </div>

                        <p className="text-sm text-brand-muted mb-3">
                          {roleData.description}
                        </p>

                        <div className="space-y-1">
                          {roleData.capabilities.map((capability, index) => (
                            <div
                              key={index}
                              className="flex items-center text-xs text-brand-muted"
                            >
                              <div className="w-1 h-1 bg-brand-muted rounded-full mr-2"></div>
                              {capability}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <SecondaryButton
                type="button"
                onClick={onClose}
                disabled={loading}
                className="!px-3 !py-2 text-sm"
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={loading}>
                {loading ? (
                  <div className="flex items-center">
                    <FaSpinner className="animate-spin h-4 w-4 mr-2" />
                    Saving...
                  </div>
                ) : (
                  "Save Changes"
                )}
              </PrimaryButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditMemberRoleModal;
